if (!customElements.get('product-quickview')) {
  class ProductQuickview extends HTMLElement {
    constructor() {
      super();

      this.openClass = 'product-quickview-is-open';
      this.dialog = this.querySelector('.product-quickview__dialog');
      this.imageEl = this.querySelector('[data-product-quickview-image]');
      this.titleEl = this.querySelector('[data-product-quickview-title]');
      this.descriptionEl = this.querySelector('[data-product-quickview-description]');
      this.variantsEl = this.querySelector('[data-product-quickview-variants]');
      this.actionsEl = this.querySelector('[data-product-quickview-actions]');
      this.priceEl = this.querySelector('[data-product-quickview-price]');
      this.soldOutEl = this.querySelector('[data-product-quickview-sold-out]');
      this.atcEl = this.querySelector('product-card-atc');
      this.popularLabel = this.dataset.mostPopularLabel || 'Meest gekozen';
      this.decreaseLabel = this.dataset.quantityDecreaseLabel || 'Decrease';
      this.increaseLabel = this.dataset.quantityIncreaseLabel || 'Increase';

      this.activeCard = null;
      this.onQuickshopOptionClick = this.onQuickshopOptionClick.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
      this.onVariantsClick = this.onVariantsClick.bind(this);

      document.addEventListener('click', this.onDocumentClick);

      this.querySelectorAll('[data-product-quickview-close]').forEach((el) => {
        el.addEventListener('click', () => this.close());
      });

      this.variantsEl?.addEventListener('click', this.onVariantsClick);
    }

    onDocumentClick(event) {
      const trigger = event.target.closest('[data-product-quickview-open]');
      if (!trigger) return;

      event.preventDefault();
      event.stopPropagation();
      this.open(trigger);
    }

    onDocumentKeydown(event) {
      if (event.key === 'Escape' && this.classList.contains(this.openClass)) {
        this.close();
      }
    }

    onVariantsClick(event) {
      const button = event.target.closest('[data-product-quickview-variant]');
      if (!button || button.disabled || button.dataset.loading) return;

      const variantId = button.dataset.productQuickviewVariant;
      const variant = this.activeData?.variants.find((v) => String(v.id) === String(variantId));
      const available = variant ? variant.available : true;

      this.syncCardVariant(variantId);
      this.atcEl?.setVariant(variantId, available);

      if (variant) this.setPrice(variant.price);
      this.setSoldOut(!available);
    }

    setPrice(value) {
      if (!this.priceEl) return;
      if (this.activeData?.isDonation || !value) {
        this.priceEl.hidden = true;
        this.priceEl.textContent = '';
        return;
      }
      this.priceEl.textContent = value;
      this.priceEl.hidden = false;
    }

    // Show the sold-out badge over the image. The add-to-cart block stays visible;
    // the shared product-card-atc swaps its button to a disabled "Sold out" button.
    setSoldOut(isSoldOut) {
      const soldOut = Boolean(isSoldOut);
      this.classList.toggle('product-quickview--sold-out', soldOut);
      if (this.soldOutEl) this.soldOutEl.hidden = !soldOut;
    }

    open(trigger) {
      const card = trigger.closest('[data-product-card]') || trigger.closest('featured-donation-product');
      if (!card) return;

      const dataEl = card.querySelector('[data-product-quickview-data]');
      if (!dataEl) return;

      let data;
      try {
        data = JSON.parse(dataEl.textContent);
      } catch (error) {
        console.error(error);
        return;
      }

      this.activeCard = card;
      this.activeData = data;
      this.populate(data, card);
      this.bindCardSync(card);

      this.classList.add(this.openClass);
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('product-quickview-open');
      document.addEventListener('keydown', this.onDocumentKeydown);
      trapFocus(this.dialog, this.querySelector('[data-product-quickview-close]'));
    }

    close() {
      this.unbindCardSync();
      this.activeCard = null;
      this.activeData = null;
      this.classList.remove(this.openClass);
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('product-quickview-open');
      document.removeEventListener('keydown', this.onDocumentKeydown);
      removeTrapFocus();
    }

    populate(data, card) {
      if (this.imageEl) {
        this.imageEl.src = data.image || '';
        this.imageEl.alt = data.title || '';
      }

      if (this.titleEl) {
        this.titleEl.textContent = data.title || '';
      }

      if (this.descriptionEl) {
        this.descriptionEl.textContent = data.description || '';
      }

      this.renderVariants(data, card);
    }

    renderVariants(data, card) {
      const hasVariants = Boolean(data.hasVariants);
      const selectedVariantId = this.getCardVariantId(card, data);

      // Donation products: each variant is its own quantity selector
      // (mirrors featured-donation-product). No shared add-to-cart actions.
      if (data.isDonation && hasVariants && this.variantsEl) {
        if (this.actionsEl) this.actionsEl.hidden = true;
        if (this.soldOutEl) this.soldOutEl.hidden = true;
        this.setPrice(null);
        this.renderDonationVariants(data);
        return;
      }

      if (this.actionsEl) {
        this.actionsEl.hidden = false;
      }

      if (!hasVariants) {
        if (this.variantsEl) {
          this.variantsEl.hidden = true;
          this.variantsEl.innerHTML = '';
        }
        const available = data.available !== false;
        this.setPrice(data.price);
        this.atcEl?.setVariant(selectedVariantId, available);
        this.setSoldOut(!available);
        return;
      }

      if (!this.variantsEl) return;

      const items = data.variants.map((variant) => {
        const popularBadge = variant.isPopular
          ? `<span class="product-quickview__popular-badge">${this.popularLabel}</span>`
          : '';

        return `
          <div class="product-quickview__variant-wrap">
            <button
              type="button"
              class="button button--purple product-quickview__variant-btn"
              data-product-quickview-variant="${variant.id}"
              ${variant.available ? '' : 'disabled'}
            >
              ${variant.label}
            </button>
            ${popularBadge}
          </div>
        `;
      }).join('');

      this.variantsEl.innerHTML = `<div class="product-quickview__variants-grid">${items}</div>`;
      this.variantsEl.hidden = false;

      const selectedVariant = data.variants.find((v) => String(v.id) === String(selectedVariantId));
      const selectedAvailable = selectedVariant ? selectedVariant.available : true;
      this.setPrice(selectedVariant ? selectedVariant.price : data.price);
      this.atcEl?.setVariant(selectedVariantId, selectedAvailable);
      this.setSoldOut(!selectedAvailable);
    }

    renderDonationVariants(data) {
      const columns = Math.min(data.variants.length, 3);
      const items = data.variants.map((variant) => this.donationVariantMarkup(variant, data.id)).join('');

      this.variantsEl.innerHTML = `<div class="product-quickview__variants-grid">${items}</div>`;
      this.variantsEl.hidden = false;
    }

    // Builds a self-contained <product-card-atc> per variant. Once inserted it
    // upgrades and syncs its quantity from the global CartState (product-card-atc.js).
    donationVariantMarkup(variant, productId) {
      const popularBadge = variant.isPopular
        ? `<span class="product-quickview__popular-badge">${this.popularLabel}</span>`
        : '';

      const minusIcon = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M12 8H4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
      const plusIcon = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M8 4V12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M12 8H4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';

      return `
        <div class="product-quickview__variant-wrap">
          <product-card-atc
            class="product-card-atc"
            data-product-id="${productId}"
            data-variant-id="${variant.id}"
            data-initial-quantity="0"
          >
            <div class="product-card-atc__actions">
              <button
                type="button"
                class="product-card-atc__add button button--purple product-quickview__variant-btn"
                data-atc-add
                ${variant.available ? '' : 'disabled'}
              >${variant.label}</button>
              <quantity-input class="product-card-atc__quantity quantity" data-atc-quantity>
                <button class="product-card-atc__quantity-button quantity__button" name="minus" type="button">
                  <span class="visually-hidden">${this.decreaseLabel}</span>
                  ${minusIcon}
                </button>
                <input
                  class="product-card-atc__quantity-input quantity__input"
                  type="number"
                  name="quantity"
                  min="0"
                  value="0"
                  pattern="[0-9]*"
                  readonly
                >
                <button class="product-card-atc__quantity-button quantity__button" name="plus" type="button">
                  <span class="visually-hidden">${this.increaseLabel}</span>
                  ${plusIcon}
                </button>
              </quantity-input>
            </div>
          </product-card-atc>
          ${popularBadge}
        </div>
      `;
    }

    getCardVariantId(card, data) {
      if (card.dataset.selectedVariantId) {
        return card.dataset.selectedVariantId;
      }

      return data.variantId;
    }

    syncCardVariant(variantId) {
      if (!this.activeCard) return;

      const quickshopOption = this.activeCard.querySelector(`[data-quickshop-option][value="${variantId}"]`);
      quickshopOption?.click();
      this.activeCard.dataset.selectedVariantId = variantId;
    }

    bindCardSync(card) {
      card.querySelectorAll('[data-quickshop-option]').forEach((option) => {
        option.addEventListener('click', this.onQuickshopOptionClick);
      });
    }

    unbindCardSync() {
      this.activeCard?.querySelectorAll('[data-quickshop-option]').forEach((option) => {
        option.removeEventListener('click', this.onQuickshopOptionClick);
      });
    }

    onQuickshopOptionClick(event) {
      const variantId = event.currentTarget.value;
      const variant = this.activeData?.variants.find((v) => String(v.id) === String(variantId));
      const available = variant ? variant.available : !event.currentTarget.disabled;
      this.atcEl?.setVariant(variantId, available);
      if (variant) this.setPrice(variant.price);
      this.setSoldOut(!available);
      if (this.activeCard) {
        this.activeCard.dataset.selectedVariantId = variantId;
      }
    }
  }

  customElements.define('product-quickview', ProductQuickview);
}
